
import { useState, useEffect, useCallback } from 'react';

/**
 * ContactPopup component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls the visibility of the popup.
 * @param {Function} props.onClose - Callback to close the popup.
 */
const ContactPopup = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaNumbers, setCaptchaNumbers] = useState({ a: 0, b: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Generate a new simple math captcha
  const generateCaptcha = useCallback(() => {
    try {
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      setCaptchaNumbers({ a, b });
      setCaptchaAnswer('');
    } catch (e) {
      console.error('Failed to generate captcha:', e);
      setError('Unable to generate security question. Please try again later.');
    }
  }, []);

  // Lock body scroll when popup is open and generate captcha
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      generateCaptcha();
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount or when isOpen changes
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, generateCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // ---- CAPTCHA VALIDATION ----
    const correctAnswer = captchaNumbers.a + captchaNumbers.b;
    if (parseInt(captchaAnswer, 10) !== correctAnswer) {
      setError('Incorrect answer. Please try again.');
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d69a8ovmvg9ih2c79u4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setIsSuccess(true);
        setEmail('');
        setMessage('');
        setCaptchaAnswer('');
        // Auto‑close after a short success display
        setTimeout(() => {
          setIsSuccess(false);
          onClose();
        }, 2000);
      } else {
        // Server returned an error status
        const errorMsg = await response.text().catch(() => '');
        setError(errorMsg || 'Something went wrong. Please try again.');
        generateCaptcha();
      }
    } catch (err) {
      // Network or unexpected error
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again.');
      generateCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Do not render anything if the popup is closed
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close contact popup"
      />
      {/* Popup container */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md p-6 animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="text-center mb-6">
          <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
            <i className="ri-magic-line text-2xl text-amber-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Get in Touch</h3>
          <p className="text-sm text-gray-500 mt-1">
            Send me a message and I&apos;ll get back to you soon!
          </p>
        </div>

        {isSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600" />
            </div>
            <p className="text-green-600 font-medium">Message sent successfully!</p>
          </div>
        ) : (
          <form
            id="contact-popup-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {/* Email field */}
            <div>
              <label
                htmlFor="popup-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Your Email
              </label>
              <input
                type="email"
                id="popup-email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Message field */}
            <div>
              <label
                htmlFor="popup-message"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Message
              </label>
              <textarea
                id="popup-message"
                name="message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                required
                rows={4}
                maxLength={500}
                placeholder="Tell me about your event or ask any questions..."
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {message.length}/500
              </p>
            </div>

            {/* CAPTCHA */}
            <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-check-line text-gray-500" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Security Check
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded border border-gray-200">
                  <span className="text-base font-semibold text-gray-800">
                    {captchaNumbers.a}
                  </span>
                  <span className="text-gray-500">+</span>
                  <span className="text-base font-semibold text-gray-800">
                    {captchaNumbers.b}
                  </span>
                  <span className="text-gray-500">=</span>
                </div>
                <input
                  type="number"
                  id="captcha-answer"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  required
                  placeholder="?"
                  className="w-20 px-3 py-2 text-sm text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={generateCaptcha}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  title="Generate new question"
                >
                  <i className="ri-refresh-line text-lg" />
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactPopup;
